// Chatbot.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import IconButton from '@mui/material/IconButton';

import { useAuth } from '../../context/AuthContext';
import { aiService } from '../../services/api/aiService';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  ChatBackdrop,
  ChatShell,
  ChatHeader,
  HeaderIcon,
  HeaderText,
  HeaderTitle,
  HeaderSubtitle,
  HeaderActions,
  MessagesContainer,
  InputContainer,
  TextInput,
  SendButton,
  CollapsedPill,
  MessageGroup,
  MessageMeta,
  AvatarDot,
  SenderName,
  TimeStamp,
  Message,
  UserBubble,
  BotBubble,
  TypingIndicator
} from './Chatbot.styled';

const WELCOME_COPY = (
  <>
    👋 Hi there! I’m your friendly NKY Neighbor. I know the best local pros around
    Northern Kentucky and I’m here to help you find the right one.
    <br />
    <br />
    What can I help you with today?
  </>
);

export const Chatbot = () => {
  const { user } = useAuth();

  const [collapsed, setCollapsed] = useState(true);
  const [mode, setMode] = useState('docked'); // 'docked' | 'fullscreen'
  // IMPORTANT: conversation ONLY (no welcome message stored here)
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef(null);

  const USER_LABEL = 'You';
  const BOT_LABEL = 'NKY Neighbor';

  const formatTime = (d = new Date()) =>
    d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const displayMessages = useMemo(() => {
    // Give each message a stable UI timestamp without mutating originals
    return messages.map(m => ({
      ...m,
      _ts: m._ts ?? Date.now(),
    }));
  }, [messages]);

  // Auto-scroll to bottom on messages or loading changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages, loading]);

  // ESC to exit fullscreen
  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape' && mode === 'fullscreen') setMode('docked');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mode]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMsg = { role: 'user', content: inputValue.trim(), _ts: Date.now() };
    const updated = [...messages, userMsg];

    setMessages(updated);
    setInputValue('');
    setLoading(true);

    const { status, data, error } = await aiService.messageChatbot(updated);

    if (status === 201 && data?.messages) {
      // If backend doesn’t provide timestamps, add them for UI
      const withTs = data.messages.map(m => ({ ...m, _ts: m._ts ?? Date.now() }));
      setMessages(withTs);
    } else {
      let errText = '';
      if (status === 429) {
        errText = 'You’ve hit your daily chat limit.';
      } else if (status === 401) {
        errText = 'Session expired; please log in again.';
        // sessionAwareFetch already redirects to /login with return path
      } else {
        errText = error || 'Oops—something went wrong. Please try again.';
      }
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: errText, _ts: Date.now() },
      ]);
    }

    setLoading(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey && user && !loading) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleFullscreen = () => {
    setMode(prev => (prev === 'fullscreen' ? 'docked' : 'fullscreen'));
  };

  // Collapsed pill
  if (collapsed) {
    return (
      <CollapsedPill
        type="button"
        onClick={() => setCollapsed(false)}
        aria-label="Open chat with NKY Neighbor"
      >
        <span className="chat-pill-icon" aria-hidden>
          <ChatBubbleIcon />
        </span>
        <span>{BOT_LABEL}</span>
      </CollapsedPill>
    );
  }

  return (
    <>
      {mode === 'fullscreen' && (
        <ChatBackdrop onClick={() => setMode('docked')} />
      )}

      <ChatShell data-mode={mode} role="dialog" aria-label="Chatbot">
        {/* Header */}
        <ChatHeader>
          <HeaderIcon aria-hidden>
            <ChatBubbleIcon />
          </HeaderIcon>
          <HeaderText>
            <HeaderTitle>{BOT_LABEL}</HeaderTitle>
            <HeaderSubtitle>Your local home services helper</HeaderSubtitle>
          </HeaderText>

          <HeaderActions>
            <IconButton
              size="small"
              onClick={toggleFullscreen}
              aria-label={mode === 'fullscreen' ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {mode === 'fullscreen' ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
            </IconButton>

            <IconButton
              size="small"
              onClick={() => setCollapsed(true)}
              aria-label="Close chat"
            >
              <CloseIcon />
            </IconButton>
          </HeaderActions>
        </ChatHeader>

        {/* Messages */}
        {!user ? (
          <MessagesContainer>
            <MessageGroup role="assistant">
              <MessageMeta>
                <AvatarDot role="assistant" />
                <SenderName>{BOT_LABEL}</SenderName>
                <TimeStamp>{formatTime()}</TimeStamp>
              </MessageMeta>

              <Message role="assistant">
                <BotBubble>
                  Please{' '}
                  <Link to="/login">
                    log in
                  </Link>{' '}
                  or{' '}
                  <Link to="/sign-up">
                    create an account
                  </Link>{' '}
                  to start chatting.
                </BotBubble>
              </Message>
            </MessageGroup>

            <div ref={messagesEndRef} />
          </MessagesContainer>
        ) : (
          <MessagesContainer>
            {/* Always show welcome (NOT in messages state, so it never duplicates) */}
            <MessageGroup role="assistant">
              <MessageMeta>
                <AvatarDot role="assistant" />
                <SenderName>{BOT_LABEL}</SenderName>
                <TimeStamp>{formatTime()}</TimeStamp>
              </MessageMeta>

              <Message role="assistant">
                <BotBubble>{WELCOME_COPY}</BotBubble>
              </Message>
            </MessageGroup>

            {/* Conversation */}
            {displayMessages.map((m, i) => {
              const isUser = m.role === 'user';
              const name = isUser ? USER_LABEL : BOT_LABEL;

              return (
                <MessageGroup key={i} role={m.role}>
                  <MessageMeta>
                    <AvatarDot role={m.role} />
                    <SenderName>{name}</SenderName>
                    <TimeStamp>{formatTime(new Date(m._ts))}</TimeStamp>
                  </MessageMeta>

                  <Message role={m.role}>
                    {isUser ? (
                      <UserBubble>{m.content}</UserBubble>
                    ) : (
                      <BotBubble>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {m.content}
                        </ReactMarkdown>
                      </BotBubble>
                    )}
                  </Message>
                </MessageGroup>
              );
            })}

            {/* Loading bubble */}
            {loading && (
              <MessageGroup role="assistant">
                <MessageMeta>
                  <AvatarDot role="assistant" />
                  <SenderName>{BOT_LABEL}</SenderName>
                  <TimeStamp>{formatTime()}</TimeStamp>
                </MessageMeta>

                <Message role="assistant">
                  <BotBubble>
                    <TypingIndicator aria-label="NKY Neighbor is typing">
                      <span />
                      <span />
                      <span />
                    </TypingIndicator>
                  </BotBubble>

                </Message>
              </MessageGroup>
            )}

            <div ref={messagesEndRef} />
          </MessagesContainer>
        )}

        {/* Input */}
        <InputContainer>
          <TextInput
            placeholder={user ? 'Type a message…' : 'Log in to chat…'}
            disabled={!user || loading}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SendButton
            onClick={handleSend}
            disabled={!user || loading || !inputValue.trim()}
          >
            <SendIcon />
          </SendButton>
        </InputContainer>
      </ChatShell>
    </>
  );
};
